import React from "react";
import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  IL_COLORS,
  formatCurrency,
  formatSignedPercent,
} from "../../../../constants/investmentLeague";

// SVG Icons
const MoneyCoinsIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#00D492" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <ellipse cx="12" cy="5" rx="9" ry="3" />
    <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
    <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
    <path d="M21 12v7" />
  </svg>
);

const ChartBarIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#00D492" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="20" x2="18" y2="10" />
    <line x1="12" y1="20" x2="12" y2="4" />
    <line x1="6" y1="20" x2="6" y2="14" />
    <line x1="2" y1="20" x2="22" y2="20" />
  </svg>
);

const CrownIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#F5C842" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 4l3 12h14l3-12-6 7-4-7-4 7-6-7z" fill="rgba(245, 200, 66, 0.2)" />
    <circle cx="12" cy="19" r="2" fill="#F5C842" />
  </svg>
);

const TrophyIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#F5C842" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 9H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h2" />
    <path d="M18 9h2a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2h-2" />
    <path d="M6 3h12v7a6 6 0 0 1-12 0V3z" fill="rgba(245, 200, 66, 0.15)" />
    <path d="M12 16v5" />
    <path d="M8 21h8" />
  </svg>
);

const SummaryCards = ({ summary, unitType = "All Units" }) => {
  const theme = useTheme();
  const t = theme.custom;

  const getInvestmentsLabel = () => {
    if (unitType === "MAC") return "TOTAL MAC INVESTMENTS";
    if (unitType === "Groups") return "TOTAL GROUP INVESTMENTS";
    return "TOTAL INVESTMENTS (ALL UNITS)";
  };

  const getMostInvestedLabel = () => {
    if (unitType === "MAC") return "MOST INVESTED MAC";
    if (unitType === "Groups") return "MOST INVESTED GROUP";
    return "MOST INVESTED UNIT";
  };

  const getHighestRoiLabel = () => {
    if (unitType === "MAC") return "HIGHEST ROI MAC";
    if (unitType === "Groups") return "HIGHEST ROI GROUP";
    return "HIGHEST ROI UNIT";
  };

  const cards = [
    {
      icon: <MoneyCoinsIcon />,
      iconBg: "rgba(0, 212, 146, 0.12)",
      label: getInvestmentsLabel(),
      value: formatCurrency(summary?.totalInvestment || 0),
      sub: `↑ +11.8% ${summary?.periodLabel || "vs previous period"}`,
      subColor: IL_COLORS.positive,
    },
    {
      icon: <ChartBarIcon />,
      iconBg: "rgba(0, 212, 146, 0.12)",
      label: "TOTAL RETURN GENERATED",
      value: formatCurrency(summary?.totalReturn || 0),
      valueColor: summary?.totalReturn >= 0 ? t.text : IL_COLORS.negative,
      sub: summary?.totalReturn >= 0
        ? `↑ +9.6% ${summary?.periodLabel || "vs previous period"}`
        : `↓ Net variance ${summary?.periodLabel || "vs previous period"}`,
      subColor: summary?.totalReturn >= 0 ? IL_COLORS.positive : IL_COLORS.negative,
    },
    {
      icon: <CrownIcon />,
      iconBg: "rgba(245, 200, 66, 0.12)",
      label: getMostInvestedLabel(),
      value: summary?.mostInvestedUnit || "-",
      sub: summary?.mostInvestedAmount ? formatCurrency(summary.mostInvestedAmount) : "-",
      subColor: t.text2,
    },
    {
      icon: <TrophyIcon />,
      iconBg: "rgba(245, 200, 66, 0.12)",
      label: getHighestRoiLabel(),
      value: summary?.highestRoiUnit || "-",
      sub: summary?.highestRoi != null ? formatSignedPercent(summary.highestRoi) : "-",
      subColor: summary?.highestRoi >= 0 ? IL_COLORS.positive : IL_COLORS.negative,
    },
  ];

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "1fr",
          sm: "repeat(2, 1fr)",
          lg: "repeat(4, 1fr)",
        },
        gap: 2,
        mb: 3,
      }}
    >
      {cards.map((card, idx) => (
        <Box
          key={idx}
          sx={{
            background: t.surface,
            border: `1px solid ${t.border}`,
            borderRadius: "10px",
            p: 2.5,
            display: "flex",
            alignItems: "center",
            gap: 2,
            transition: "border-color 0.15s ease",
            "&:hover": {
              borderColor: t.border2,
            },
          }}
        >
          <Box
            sx={{
              width: 52,
              height: 52,
              borderRadius: "50%",
              background: card.iconBg,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            {card.icon}
          </Box>

          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              sx={{
                fontSize: "10px",
                fontWeight: 600,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                color: t.text3,
                mb: 0.5,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {card.label}
            </Typography>

            <Typography
              sx={{
                fontSize: { xs: "18px", md: "20px" },
                fontWeight: 700,
                color: card.valueColor || t.text,
                lineHeight: 1.2,
                mb: 0.5,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
              title={card.value}
            >
              {card.value}
            </Typography>

            <Typography
              sx={{
                fontSize: "11px",
                color: card.subColor || t.text3,
                fontWeight: 500,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {card.sub}
            </Typography>
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export default SummaryCards;

